import { rd } from '@passionware/monads';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Dialog, DialogContentHeadless } from '../../design-system/Dialog';
import { WithServices } from '../../platform/ts/services';
import { WithAccessService } from '../../services/front/AccessService/AccessService';
import { WithFeatureManagementService } from '../../services/front/FeatureManagementService/FeatureManagementService';
import { WithUserPreferencesService } from '../../services/front/UserPreferencesService/UserPreferencesService';
import { WithAppScopeService } from '../../services/internal/AppScopeService/AppScopeService';
import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { FeatureFlagCatalogView } from './catalog/FeatureFlagCatalogView';
import { toggleStarredFeatureId } from './catalog/buildFeatureFlagCatalog';

function FeatureFlagCatalogDialogContent(
  props: WithServices<
    [WithFeatureManagementService, WithUserPreferencesService]
  > & {
    onClose: () => void;
  }
) {
  const { featureManagementService, userPreferencesService } = props.services;
  const view = featureManagementService.useFeatureFlagConfigView();
  const preferenceRd = userPreferencesService.featureFlagManager.useValue();
  const preference = rd.tryGet(preferenceRd);
  useRegisterDirtyState(rd.tryGet(view)?.isDirty ?? false);

  if (!preference) {
    return (
      <FeatureFlagCatalogView
        view={rd.ofPending()}
        onAction={featureManagementService.scheduleAction}
        onSave={() => Promise.resolve()}
        search=""
        onSearchChange={() => undefined}
        starredFeatureIds={[]}
        onToggleStarred={() => undefined}
        browseBy="feature"
        onBrowseByChange={() => undefined}
      />
    );
  }

  const setPreference = (patch: Partial<typeof preference>) => {
    userPreferencesService.featureFlagManager.setValue({
      ...preference,
      ...patch
    });
  };

  return (
    <FeatureFlagCatalogView
      view={view}
      onAction={featureManagementService.scheduleAction}
      onSave={() => featureManagementService.saveConfig().then(props.onClose)}
      search={preference.search}
      onSearchChange={search => setPreference({ search })}
      starredFeatureIds={preference.starredFeatureIds}
      onToggleStarred={featureId =>
        setPreference({
          starredFeatureIds: toggleStarredFeatureId(
            preference.starredFeatureIds,
            featureId
          )
        })
      }
      browseBy={preference.browseBy}
      onBrowseByChange={browseBy => setPreference({ browseBy })}
    />
  );
}

function FeatureFlagCatalogShell(
  props: WithServices<
    [
      WithFeatureManagementService,
      WithAccessService,
      WithAppScopeService,
      WithUserPreferencesService
    ]
  > & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    canManageFeatureFlags: boolean | undefined;
  }
) {
  const { featureManagementService } = props.services;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      featureManagementService.discardChanges();
    }
    props.onOpenChange(open);
  };

  useHotkeys('ctrl+shift+l', () => {
    if (props.canManageFeatureFlags) {
      handleOpenChange(!props.open);
    }
  });

  return (
    <UnsavedChangesProtection open={props.open} onOpenChange={handleOpenChange}>
      {dialogProps => (
        <Dialog {...dialogProps}>
          <DialogContentHeadless>
            <FeatureFlagCatalogDialogContent
              services={props.services}
              onClose={() => handleOpenChange(false)}
            />
          </DialogContentHeadless>
        </Dialog>
      )}
    </UnsavedChangesProtection>
  );
}

export function FeatureFlagWidget(
  props: WithServices<
    [
      WithFeatureManagementService,
      WithAccessService,
      WithAppScopeService,
      WithUserPreferencesService
    ]
  >
) {
  const [show, setShow] = useState(false);
  const can = props.services.accessService.useCan(
    props.services.appScopeService.useOrganizationScopeIdentity()
  );
  const canManageFeatureFlags = rd.tryGet(
    can({ action: 'manage_feature_flags' })
  );

  return (
    <FeatureFlagCatalogShell
      services={props.services}
      open={show}
      onOpenChange={setShow}
      canManageFeatureFlags={canManageFeatureFlags}
    />
  );
}
